/**
 * HarnessInputForm — bespoke JSON-Schema → React form (§19.1).
 *
 * NOT @rjsf/core (Decision 1 in lexportal-implementation-plan §19.1):
 * the harnesses we ship today (`selftest`, `echo`, `resume-selftest`,
 * `tool-use-demo`) use flat object schemas. This renderer covers the
 * seven shapes the plan enumerates:
 *
 *   string · number · integer · boolean · string+enum ·
 *   array<string> · nested object (one level)
 *
 * Anything it can't model (oneOf / anyOf / $ref / array<object> / …)
 * falls back to a raw-JSON `<textarea>` for the WHOLE form, with a
 * hint. Per-field server-side validation errors (the harn-service
 * `schema_validation_failed` 400 body) render inline under each field.
 */

import { useMemo } from "react";

type JsonSchema = Record<string, unknown>;

export interface HarnessInputFormProps {
  /** The harness manifest's `input_schema` (raw JSON Schema, may be undefined). */
  schema: JsonSchema | undefined;
  /** Current form value (object keyed by property name, or raw for fallback). */
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** Raw-JSON text buffer (used only in the textarea-fallback path). */
  rawText: string;
  onRawTextChange: (next: string) => void;
  /** Per-field server-side errors keyed by property name (or `__root__`). */
  fieldErrors?: Record<string, string>;
  disabled?: boolean;
}

interface PropSpec {
  name: string;
  schema: JsonSchema;
  required: boolean;
}

function asSchema(v: unknown): JsonSchema | undefined {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as JsonSchema) : undefined;
}

/**
 * True when the schema is a flat/one-level object we can render
 * natively. Exported so the runner panel resolves the submit payload
 * (form value vs raw-JSON buffer) with the EXACT same predicate the
 * renderer used — a looser check there would silently submit an empty
 * object when the form actually fell back to the raw textarea.
 */
export function isRenderableSchema(schema: JsonSchema | undefined): boolean {
  if (!schema) return false;
  if (schema.type !== "object") return false;
  const props = asSchema(schema.properties);
  if (!props || Object.keys(props).length === 0) return false;
  // Bail to raw JSON if any prop uses a shape we don't model.
  for (const raw of Object.values(props)) {
    const p = asSchema(raw);
    if (!p) return false;
    if (p.oneOf || p.anyOf || p.allOf || p.$ref) return false;
    const t = p.type;
    if (t === "array") {
      const items = asSchema(p.items);
      if (!items || items.type !== "string") return false; // only array<string>
    } else if (t === "object") {
      // one level of nesting only — the nested object's props must be scalars
      const np = asSchema(p.properties);
      if (!np) return false;
      for (const nraw of Object.values(np)) {
        const n = asSchema(nraw);
        if (!n || n.type === "object" || n.type === "array") return false;
      }
    } else if (t !== "string" && t !== "number" && t !== "integer" && t !== "boolean") {
      return false;
    }
  }
  return true;
}

function collectProps(schema: JsonSchema): PropSpec[] {
  const props = asSchema(schema.properties) ?? {};
  const required = new Set(
    Array.isArray(schema.required) ? (schema.required as string[]) : [],
  );
  return Object.entries(props).map(([name, raw]) => ({
    name,
    schema: asSchema(raw) ?? {},
    required: required.has(name),
  }));
}

function Label({ spec }: { spec: PropSpec }) {
  return (
    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
      {spec.name}
      {spec.required && <span className="ml-0.5 text-red-500">*</span>}
      {typeof spec.schema.description === "string" && (
        <span className="ml-1 font-normal text-slate-400">— {spec.schema.description}</span>
      )}
    </label>
  );
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <p className="mt-0.5 text-[11px] text-red-600 dark:text-red-400">{msg}</p>;
}

const inputCls =
  "w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-indigo-400 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900";

function ScalarField({
  spec,
  value,
  onChange,
  error,
  disabled,
  testIdPrefix,
}: {
  spec: PropSpec;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
  disabled?: boolean;
  testIdPrefix: string;
}) {
  const { schema } = spec;
  const t = schema.type as string;
  const testId = `${testIdPrefix}-${spec.name}`;

  if (t === "boolean") {
    return (
      <div className="space-y-1">
        <label className="flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            data-testid={testId}
          />
          {spec.name}
          {spec.required && <span className="text-red-500">*</span>}
          {typeof schema.description === "string" && (
            <span className="font-normal text-slate-400">— {schema.description}</span>
          )}
        </label>
        <FieldError msg={error} />
      </div>
    );
  }

  if (t === "string" && Array.isArray(schema.enum)) {
    return (
      <div className="space-y-1">
        <Label spec={spec} />
        <select
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={inputCls}
          data-testid={testId}
        >
          <option value="">— select —</option>
          {(schema.enum as unknown[]).map((opt) => (
            <option key={String(opt)} value={String(opt)}>
              {String(opt)}
            </option>
          ))}
        </select>
        <FieldError msg={error} />
      </div>
    );
  }

  if (t === "number" || t === "integer") {
    return (
      <div className="space-y-1">
        <Label spec={spec} />
        <input
          type="number"
          step={t === "integer" ? 1 : "any"}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return onChange(undefined);
            const n = t === "integer" ? parseInt(raw, 10) : parseFloat(raw);
            onChange(Number.isNaN(n) ? undefined : n);
          }}
          disabled={disabled}
          className={inputCls}
          data-testid={testId}
        />
        <FieldError msg={error} />
      </div>
    );
  }

  // default: string
  return (
    <div className="space-y-1">
      <Label spec={spec} />
      <input
        type="text"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={inputCls}
        data-testid={testId}
      />
      <FieldError msg={error} />
    </div>
  );
}

export function HarnessInputForm({
  schema,
  value,
  onChange,
  rawText,
  onRawTextChange,
  fieldErrors,
  disabled,
}: HarnessInputFormProps) {
  const renderable = useMemo(() => isRenderableSchema(schema), [schema]);

  if (!renderable) {
    return (
      <div className="space-y-1" data-testid="harness-input-raw">
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
          Input (raw JSON)
          <span className="ml-1 font-normal text-slate-400">
            — this harness's schema isn't form-renderable; provide JSON directly
          </span>
        </label>
        <textarea
          value={rawText}
          onChange={(e) => onRawTextChange(e.target.value)}
          disabled={disabled}
          rows={6}
          spellCheck={false}
          placeholder="{}"
          className={`${inputCls} font-mono`}
          data-testid="harness-input-raw-textarea"
        />
        <FieldError msg={fieldErrors?.__root__} />
      </div>
    );
  }

  const specs = collectProps(schema as JsonSchema);
  const setField = (name: string, v: unknown) => {
    const next = { ...value };
    if (v === undefined) delete next[name];
    else next[name] = v;
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="harness-input-form">
      {specs.map((spec) => {
        const err = fieldErrors?.[spec.name];
        if (spec.schema.type === "object") {
          const nested = collectProps(spec.schema);
          const nestedVal = asSchema(value[spec.name]) ?? {};
          return (
            <fieldset
              key={spec.name}
              className="rounded-md border border-slate-200 p-3 dark:border-slate-700"
              data-testid={`harness-field-object-${spec.name}`}
            >
              <legend className="px-1 text-xs font-medium text-slate-600 dark:text-slate-300">
                {spec.name}
                {spec.required && <span className="ml-0.5 text-red-500">*</span>}
              </legend>
              <div className="space-y-3 pt-1">
                {nested.map((ns) => (
                  <ScalarField
                    key={ns.name}
                    spec={ns}
                    value={nestedVal[ns.name]}
                    onChange={(v) => {
                      const nx = { ...nestedVal };
                      if (v === undefined) delete nx[ns.name];
                      else nx[ns.name] = v;
                      setField(spec.name, Object.keys(nx).length ? nx : undefined);
                    }}
                    error={fieldErrors?.[`${spec.name}.${ns.name}`]}
                    disabled={disabled}
                    testIdPrefix={`harness-field-${spec.name}`}
                  />
                ))}
              </div>
              <FieldError msg={err} />
            </fieldset>
          );
        }
        if (spec.schema.type === "array") {
          // array<string> — comma-or-newline separated entry
          const arr = Array.isArray(value[spec.name])
            ? (value[spec.name] as unknown[])
            : [];
          return (
            <div key={spec.name} className="space-y-1">
              <Label spec={spec} />
              <textarea
                value={arr.join("\n")}
                onChange={(e) => {
                  const items = e.target.value
                    .split(/[\n,]/)
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);
                  setField(spec.name, items.length ? items : undefined);
                }}
                disabled={disabled}
                rows={3}
                placeholder="one value per line"
                className={inputCls}
                data-testid={`harness-field-${spec.name}`}
              />
              <FieldError msg={err} />
            </div>
          );
        }
        return (
          <ScalarField
            key={spec.name}
            spec={spec}
            value={value[spec.name]}
            onChange={(v) => setField(spec.name, v)}
            error={err}
            disabled={disabled}
            testIdPrefix="harness-field"
          />
        );
      })}
    </div>
  );
}
